import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { setAuthToken } from "../api/api";
import "./login.css";
import heroImg from "../assets/home health care.png";
import Header from "../components/layout/Header";

const BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://api.suvidhaeldercare.com";

const COUNTRIES = [
  { code: "+91",  flag: "🇮🇳", name: "India",   digits: 10 },
  { code: "+1",   flag: "🇺🇸", name: "USA",     digits: 10 },
  { code: "+44",  flag: "🇬🇧", name: "UK",      digits: 10 },
  { code: "+61",  flag: "🇦🇺", name: "Australia", digits: 9 },
  { code: "+971", flag: "🇦🇪", name: "UAE",     digits: 9  },
  { code: "+65",  flag: "🇸🇬", name: "Singapore", digits: 8 },
  { code: "+60",  flag: "🇲🇾", name: "Malaysia", digits: 9 },
];

/* ── Small reusable error box ──────────────────── */
function ErrBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      background: "#FEF2F2", border: "1px solid #FECACA",
      borderRadius: 10, padding: "10px 14px",
      fontSize: 13, color: "#DC2626", fontWeight: 600
    }}>⚠️ {msg}</div>
  );
}

/* ── OTP digit boxes ───────────────────────────── */
function OtpInput({ value, onChange, disabled }) {
  const r0 = useRef(null); const r1 = useRef(null); const r2 = useRef(null);
  const r3 = useRef(null); const r4 = useRef(null); const r5 = useRef(null);
  const refs = [r0, r1, r2, r3, r4, r5];
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs[i - 1].current?.focus();
      return;
    }
    if (e.key === "ArrowLeft"  && i > 0) { refs[i - 1].current?.focus(); return; }
    if (e.key === "ArrowRight" && i < 5) { refs[i + 1].current?.focus(); return; }
    if (!/^\d$/.test(e.key)) return;
    const next = (value.slice(0, i) + e.key + value.slice(i + 1)).slice(0, 6);
    onChange(next);
    if (i < 5) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) { onChange(pasted); refs[Math.min(pasted.length, 5)].current?.focus(); }
    e.preventDefault();
  };

  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center", margin: "8px 0" }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onChange={() => {}}
          onFocus={e => e.target.select()}
          style={{
            width: 44, height: 52, textAlign: "center",
            fontSize: 22, fontWeight: 800,
            border: `2px solid ${d ? "#1F4E8C" : "#E2E8F0"}`,
            borderRadius: 12,
            background: d ? "#EFF6FF" : "#F8FAFC",
            color: "#0F2A4A", outline: "none",
            transition: "all .15s",
            opacity: disabled ? 0.6 : 1
          }}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN LOGIN COMPONENT
══════════════════════════════════════════════════ */
export default function Login() {
  // ── Steps: "phone" | "otp" | "admin"
  const [step,         setStep]         = useState("phone");

  // Phone step
  const [countryCode,  setCountryCode]  = useState("+91");
  const [phone,        setPhone]        = useState("");
  const [fullName,     setFullName]     = useState("");

  // OTP step
  const [otp,          setOtp]          = useState("");
  const [timer,        setTimer]        = useState(30);
  const [canResend,    setCanResend]    = useState(false);

  // Admin step (password fallback)
  const [password,     setPassword]     = useState("");
  const [showPass,     setShowPass]     = useState(false);

  // Shared
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");

  // Optional checkboxes
  const [insuranceOk,  setInsuranceOk]  = useState(false);
  const [promotionsOk, setPromotionsOk] = useState(true);

  const navigate  = useNavigate();
  const location  = useLocation();
  const timerRef  = useRef(null);

  const country = COUNTRIES.find(c => c.code === countryCode) || COUNTRIES[0];

  // ── Timer countdown ─────────────────────────────
  useEffect(() => {
    if (step !== "otp") return;
    setTimer(30); setCanResend(false);
    timerRef.current = setInterval(() => {
      setTimer(t => {
        if (t <= 1) { clearInterval(timerRef.current); setCanResend(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [step]);

  // ── After login success ─────────────────────────
  const afterLogin = (token, user) => {
    localStorage.setItem("token",                token);
    localStorage.setItem("user",                 JSON.stringify(user));
    localStorage.setItem("insurance_applicable", insuranceOk);
    localStorage.setItem("promotions_accepted",  promotionsOk);
    setAuthToken(token);

    if (user?.role === "admin" || user?.role === "super_admin") {
      navigate("/admin/dashboard"); return;
    }
    if (!user?.survey_done) {
      navigate("/survey/care_needs?stepNo=1"); return;
    }
    const from    = location.state?.from;
    const service = location.state?.service;
    if (from && service) {
      navigate(from, { state: { service: service.title, service_id: service.id || null } });
    } else {
      navigate(from || "/");
    }
  };

  // ── Step 1: Send OTP ────────────────────────────
  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    const digits = country.digits;
    const re     = new RegExp(`^[0-9]{${digits}}$`);
    if (!re.test(phone)) {
      setError(`Enter a valid ${digits}-digit phone number for ${country.name}`);
      return;
    }
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: `${countryCode}${phone}` }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to send OTP");
      setOtp("");
      setStep("otp");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ──────────────────────────
  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    setError("");
    if (otp.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: `${countryCode}${phone}`,
          otp,
          full_name: fullName.trim() || null,
          insurance_applicable: insuranceOk,
          promotions_accepted:  promotionsOk,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "OTP verification failed");
      afterLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Auto-verify when all 6 digits entered ───────
  useEffect(() => {
    if (otp.length === 6 && step === "otp") handleVerifyOtp();
  }, [otp]);

  // ── Admin password login ─────────────────────────
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!password) { setError("Enter your password"); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Login failed");
      afterLogin(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ──────────────────────────────────
  const handleResend = () => {
    if (!canResend) return;
    setOtp(""); setError("");
    handleSendOtp();
  };

  const maskedPhone = phone.length >= 4
    ? `${countryCode} ${"•".repeat(phone.length - 3)}${phone.slice(-3)}`
    : `${countryCode} ${phone}`;

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
   <>
   <Header/>
    <div className="suvi-login">
      {/* LEFT IMAGE */}
      <div className="suvi-left">
        <img className="suvi-img" src={heroImg} alt="Suvidha Elder Care" />
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          background: "linear-gradient(to top, rgba(15,42,74,.9), transparent)",
          padding: "40px 32px 32px", color: "#fff"
        }}>
          <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>
            Care that comes home 🏠
          </div>
          <div style={{ fontSize: 14, opacity: .85, lineHeight: 1.7 }}>
            Trusted elder care services across India, USA, UK & UAE
          </div>
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="suvi-right">
        <div className="suvi-box">

          {/* Brand */}
          <div className="suvi-brand">
            <div className="suvi-logoDot" />
            <span className="suvi-brandName">Suvidha Elder Care</span>
          </div>

          {/* ── STEP: PHONE ─────────────────────── */}
          {step === "phone" && (
            <>
              <h2 className="suvi-title">Welcome back</h2>
              <p className="suvi-subtitle">Enter your phone number to receive a one-time password</p>

              <form onSubmit={handleSendOtp} className="suvi-form">
                <ErrBox msg={error} />

                <label className="suvi-label">
                  Full Name
                  <div className="suvi-passWrap">
                    <input
                      className="suvi-input"
                      placeholder="Your full name"
                      type="text"
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      autoComplete="name"
                    />
                  </div>
                </label>

                <label className="suvi-label">
                  Phone Number
                  <div className="suvi-phoneWrap">
                    <select
                      className="suvi-country"
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </option>
                      ))}
                    </select>
                    <input
                      className="suvi-input"
                      placeholder={`${country.digits}-digit number`}
                      inputMode="numeric"
                      maxLength={country.digits}
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                      autoComplete="tel"
                      autoFocus
                    />
                  </div>
                </label>

                {/* Optional checkboxes */}
                <div className="suvi-checks">
                  <label className="suvi-check-row">
                    <input type="checkbox" className="suvi-checkbox"
                      checked={insuranceOk} onChange={e => setInsuranceOk(e.target.checked)} />
                    <span className="suvi-check-text">
                      Insurance is applicable for my care plan
                    </span>
                  </label>
                  <label className="suvi-check-row">
                    <input type="checkbox" className="suvi-checkbox"
                      checked={promotionsOk} onChange={e => setPromotionsOk(e.target.checked)} />
                    <span className="suvi-check-text">
                      I accept to receive promotional updates from Suvidha
                    </span>
                  </label>
                </div>

                <button className="suvi-primaryBtn" type="submit" disabled={loading || phone.length < country.digits}>
                  {loading ? "Sending OTP..." : "Send OTP →"}
                </button>

                <p className="suvi-terms">
                  By continuing you agree to our{" "}
                  <Link to="/terms" className="suvi-termsLink">Terms & Conditions</Link>{" "}
                  and{" "}
                  <Link to="/terms?tab=privacy" className="suvi-termsLink">Privacy Policy</Link>.
                </p>

                <p className="suvi-bottomText" style={{ textAlign: "center", margin: "4px 0 0" }}>
                  New here?{" "}
                  <Link to="/signup" className="suvi-link">Create an account</Link>
                </p>

                {/* Admin fallback */}
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <button type="button" className="suvi-linkBtn"
                    onClick={() => { setError(""); setStep("admin"); }}>
                    Admin? Use password login
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── STEP: OTP ───────────────────────── */}
          {step === "otp" && (
            <>
              <h2 className="suvi-title">Enter OTP</h2>
              <p className="suvi-subtitle">
                We sent a 6-digit code to <strong>{maskedPhone}</strong>
              </p>

              <form onSubmit={handleVerifyOtp} className="suvi-form">
                <ErrBox msg={error} />

                <OtpInput value={otp} onChange={setOtp} disabled={loading} />

                <button className="suvi-primaryBtn" type="submit"
                  disabled={loading || otp.length !== 6}>
                  {loading ? "Verifying..." : "Verify OTP ✓"}
                </button>

                {/* Resend + back */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                  <button type="button" className="suvi-linkBtn"
                    onClick={() => { setStep("phone"); setOtp(""); setError(""); }}>
                    ← Change number
                  </button>
                  <button type="button" className="suvi-linkBtn"
                    onClick={handleResend}
                    disabled={!canResend}
                    style={{ opacity: canResend ? 1 : 0.5 }}>
                    {canResend ? "Resend OTP" : `Resend in ${timer}s`}
                  </button>
                </div>

                <div className="suvi-note">
                  📱 OTP sent via SMS. Valid for <strong>10 minutes</strong>. Do not share it with anyone.
                </div>
              </form>
            </>
          )}

          {/* ── STEP: ADMIN PASSWORD ─────────────── */}
          {step === "admin" && (
            <>
              <h2 className="suvi-title">Admin Login</h2>
              <p className="suvi-subtitle">Enter phone number and password</p>

              <form onSubmit={handleAdminLogin} className="suvi-form">
                <ErrBox msg={error} />

                <label className="suvi-label">
                  Phone Number
                  <div className="suvi-phoneWrap">
                    <select className="suvi-country" value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}>
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                    <input className="suvi-input" placeholder="Phone number"
                      inputMode="numeric" maxLength={10} value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                      autoFocus />
                  </div>
                </label>

                <label className="suvi-label">
                  Password
                  <div className="suvi-passWrap">
                    <input className="suvi-input" placeholder="Enter password"
                      type={showPass ? "text" : "password"} value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="current-password" />
                    <button type="button" className="suvi-ghostBtn"
                      onClick={() => setShowPass(s => !s)}>
                      {showPass ? "Hide" : "Show"}
                    </button>
                  </div>
                </label>

                <div className="suvi-actions">
                  <button className="suvi-primaryBtn" type="submit" disabled={loading}>
                    {loading ? "Logging in..." : "Login"}
                  </button>
                  <button type="button" className="suvi-linkBtn"
                    onClick={() => { setStep("phone"); setError(""); setPassword(""); }}>
                    ← Use OTP instead
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
   </>
  );
}